
import type { GridRow, GridColumn } from '../types/gridTypes';

/**
 * Computes aggregations for all rows that have aggregationConfig enabled.
 * To avoid double-counting nested groups, this simple implementation sums
 * all descendants that are NOT themselves aggregated rows (or 'group'/'subtotal'/'total' types).
 */
export const computeRowAggregations = (
  rows: GridRow[],
  columns: GridColumn[]
): GridRow[] => {
  // First, identify rows that need aggregation
  const aggregatorRows = rows.filter(r => r.aggregationConfig?.enabled);
  if (aggregatorRows.length === 0) return rows;

  // Create a map for quick parent lookup if needed, though we primarily need to find descendants
  // Optimization: Map parentId -> childIds
  const childrenMap = new Map<string, string[]>();
  rows.forEach(r => {
    if (r.parentId) {
      if (!childrenMap.has(r.parentId)) childrenMap.set(r.parentId, []);
      childrenMap.get(r.parentId)!.push(r.id);
    }
  });

  const rowMap = new Map(rows.map(r => [r.id, r]));

  // Helper to get all relevant descendants for aggregation
  // We exclude other aggregators to prevent double counting in a flattened sum
  const getContributingDescendants = (rowId: string): GridRow[] => {
    const result: GridRow[] = [];
    const queue = [...(childrenMap.get(rowId) || [])];

    while (queue.length > 0) {
      const currentId = queue.shift()!;
      const current = rowMap.get(currentId);
      if (!current) continue;

      // Check if this row is an aggregator
      const isAggregator = 
        current.aggregationConfig?.enabled || 
        ['group', 'subtotal', 'total'].includes(current.rowType || '');

      // If it's NOT an aggregator, it contributes to the sum
      if (!isAggregator) {
        result.push(current);
      }

      // We still traverse children regardless of whether this node contributes,
      // because a Group might contain a Subtotal (don't count) which contains Tasks (count).
      // WAIT: If we count Tasks inside a Subtotal, and we are a Parent Group, 
      // we are calculating (Group A -> Subtotal B -> Tasks).
      // If Subtotal B sums Tasks, and Group A sums Tasks, they are consistent.
      // If Group A sums Subtotal B, we rely on B being accurate.
      //
      // V1 Strategy from spec: "aggregate leaf/non-aggregate rows"
      // This implies we look PAST nested aggregators to the raw data.
      
      if (childrenMap.has(currentId)) {
        queue.push(...childrenMap.get(currentId)!);
      }
    }
    return result;
  };

  // Create a copy of rows to update
  const updatedRowsMap = new Map(rows.map(r => [r.id, { ...r, values: { ...r.values } }]));

  // Perform aggregation
  aggregatorRows.forEach(targetRow => {
    const descendants = getContributingDescendants(targetRow.id);
    const config = targetRow.aggregationConfig!;
    const rowToUpdate = updatedRowsMap.get(targetRow.id)!;

    columns.forEach(col => {
      const mode = config.perColumn?.[col.id] || 'none';
      if (mode === 'none') return;

      const values = descendants.map(d => {
         const v = d.values[col.id];
         // Parse if string
         if (typeof v === 'string' && v.trim() !== '') {
             const f = parseFloat(v);
             return isNaN(f) ? null : f;
         }
         return typeof v === 'number' ? v : null;
      }).filter((v): v is number => v !== null);

      let result: number | null = null;

      switch (mode) {
        case 'sum':
          result = values.reduce((a, b) => a + b, 0);
          break;
        case 'avg':
          result = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
          break;
        case 'min':
          result = values.length > 0 ? Math.min(...values) : 0;
          break;
        case 'max':
          result = values.length > 0 ? Math.max(...values) : 0;
          break;
        case 'count':
           // Count non-null/non-empty original values
           result = descendants.filter(d => {
               const v = d.values[col.id];
               return v !== null && v !== undefined && v !== '';
           }).length;
           break;
      }

      if (result !== null) {
         rowToUpdate.values[col.id] = result;
         // Clear any formula on this cell to prevent conflict, or simply let value override
         if (rowToUpdate.formulas && rowToUpdate.formulas[col.id]) {
             const newFormulas = { ...rowToUpdate.formulas };
             delete newFormulas[col.id];
             rowToUpdate.formulas = newFormulas;
         }
      }
    });
  });

  return Array.from(updatedRowsMap.values());
};
